/**
 * V2 Category leaderboard — the shared fetch plus the rivalry math
 * (2026-07-19, the "encourage people to challenge each other" ask).
 *
 * The get_category_leaderboard RPC returns the top rows PLUS the
 * caller's own row, and — once supabase/migrations/20260719b_leaderboard_rivals.sql has
 * run — a `rank` position on every row and the caller's immediate
 * neighbors (the rows just above and just below them). Until that SQL
 * runs the rows arrive without `rank`, and everything here that needs
 * positions quietly stays away — the lists/trophies degradation
 * pattern.
 *
 * Gap lines work on location COUNTS, not positions: "2 more and you
 * pass Ada" stays true whatever the tiebreak order says.
 */
import * as gateway from '../../core/gateway.js';
import { cached } from '../../core/query.js';
import { showToast } from '../../ui/toast.js';

/**
 * Top Collectors via the get_category_leaderboard RPC
 * (supabase/migrations/20260719b_leaderboard_rivals.sql): the aggregation runs server-side,
 * so the numbers stay right past 1000 yoinks — the old client-side
 * count over a capped rawQuery went silently wrong at scale
 * (assessment 1.5). Rows arrive ordered best-first.
 */
export async function fetchCategoryLeaderboard(categoryId) {
  return cached(`category-leaderboard:${categoryId}`, async () => {
    const { data, error } = await gateway.rawRpc('get_category_leaderboard', {
      params: { p_category_id: categoryId, p_limit: 10 },
    });
    if (error) throw error;
    return (data || []).map((r) => {
      const rank = r.rank == null ? NaN : Number(r.rank);
      return {
        userId: r.user_id,
        name: r.full_name || r.username || 'Explorer',
        username: r.username || '',
        locationCount: Number(r.location_count) || 0,
        rank: Number.isFinite(rank) ? rank : null,
      };
    });
  });
}

/**
 * Where a user sits on the board: their row, the leader, and the
 * immediate rivals. above/below need server ranks — they stay null on
 * pre-rivals RPC rows. Pure — exported for tests.
 */
export function standing(entries, userId) {
  const rows = entries || [];
  const mine = userId ? rows.find((e) => e.userId === userId) || null : null;
  const top = rows[0] || null; // the RPC orders best-first
  const above = mine?.rank ? rows.find((e) => e.rank === mine.rank - 1) || null : null;
  const below = mine?.rank ? rows.find((e) => e.rank === mine.rank + 1) || null : null;
  return { mine, top, above, below };
}

/** Places needed to strictly pass a rival (ties break on order, so +1). */
export function gapToPass(mine, other) {
  return other.locationCount - mine.locationCount + 1;
}

/** The brag a Challenge share hands off. Pure — exported for tests. */
export function challengeText(categoryName, mine) {
  const places = `${mine.locationCount} place${mine.locationCount === 1 ? '' : 's'}`;
  return mine.rank != null
    ? `I'm #${mine.rank} in ${categoryName} on Yoink Adventures with ${places} — think you can catch me?`
    : `I've collected ${places} in ${categoryName} on Yoink Adventures — think you can catch me?`;
}

/**
 * The one post-save message worth a toast, or null. Priority: taking
 * the top spot, entering the top 3 (a trophy position), passing
 * someone, then "almost there" when the rival above is within reach.
 * Needs server ranks on the after rows AND a real before snapshot —
 * without either it stays quiet rather than guessing (a failed
 * before-fetch must not re-celebrate a rank you already held).
 * Pure — exported for tests.
 */
export function rankNudge({ before, after, userId, categoryName }) {
  if (!Array.isArray(before)) return null;
  const now = standing(after, userId);
  if (!now.mine || now.mine.rank == null) return null;
  if (!(after || []).some((e) => e.userId !== userId)) return null; // no competition
  const prev = standing(before, userId);
  const prevRank = prev.mine?.rank ?? null;
  const rank = now.mine.rank;
  if (rank === 1 && prevRank !== 1) {
    return { text: `You're now the top collector in ${categoryName}!`, celebrate: true };
  }
  if (rank <= 3 && (prevRank == null || prevRank > 3)) {
    return { text: `Trophy position — you're #${rank} in ${categoryName}!`, celebrate: true };
  }
  if (prevRank != null && rank < prevRank) {
    // The row now just below is who this save passed — but only name
    // them when the before snapshot proves they really were ahead
    const passed = now.below &&
      before.some((e) => e.userId === now.below.userId && e.rank != null && e.rank < prevRank)
      ? now.below.name : null;
    return {
      text: passed
        ? `You passed ${passed} — #${rank} in ${categoryName}!`
        : `You're up to #${rank} in ${categoryName}!`,
      celebrate: true,
    };
  }
  if (now.above && now.mine.locationCount > (prev.mine?.locationCount ?? -1)) {
    const gap = gapToPass(now.mine, now.above);
    if (gap <= 2) {
      return {
        text: `${gap} more place${gap === 1 ? '' : 's'} in ${categoryName} and you pass ${now.above.name}.`,
        celebrate: false,
      };
    }
  }
  return null;
}

/**
 * Post-save (log flow): compare the pre-save standings snapshot with a
 * fresh read and toast the one message that matters — tappable through
 * to the category page, where the board shows the new order. Callers
 * fire and forget; every failure path resolves null.
 */
export async function announceRankChange({ store, router, categoryId, categoryName, slug, before }) {
  const me = store.get().user;
  if (!me || !Array.isArray(before)) return null;
  let after;
  try {
    after = await fetchCategoryLeaderboard(categoryId);
  } catch {
    return null;
  }
  const nudge = rankNudge({ before, after, userId: me.id, categoryName });
  if (!nudge) return null;
  showToast(nudge.text, {
    type: nudge.celebrate ? 'celebrate' : 'info',
    duration: 9000,
    onClick: slug && router ? () => router.navigate('category', { slug }) : null,
  });
  return nudge;
}
